/**
 * AddSessionModal — modal for creating a new production session.
 * Supports two input modes (weight-based and box-based) with raw material
 * selection, yield factor display, and packing material sufficiency checks.
 */
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import { X, Check, Loader2, Scale, Package, Plus, Search } from 'lucide-react';
import productionService, { type RawMaterial, type MaterialSummary } from '../../../services/productionService';
import api from '../../../services/api';
import inventoryService from '../../../services/inventoryService';
import inputOutputMappingService from '../../../services/inputOutputMappingService';
import byproductMappingService, { type ByproductMapping } from '../../../services/byproductMappingService';
import packingMaterialMappingService, { type PackingMaterialForSession } from '../../../services/packingMaterialMappingService';
import workScheduleService from '../../../services/workScheduleService';
import { useLanguage } from '../../../i18n/LanguageContext';
import { formatYield } from '../../../hooks/useYieldFactor';
import { getBusinessDayBefore } from '../../../utils/dateUtils';
import type { Product, WeightMaterialEntry, BoxMaterialEntry, NewScheduleRow, InputMode, MaterialSortOption, SelectedLabourFactorMaterial } from './AddSessionModal/types';
import WeightMaterialSection, {
  createEmptyWeightEntry,
  calculateWeightTotals,
  calculateEstimatedBoxes,
  isWeightFormValid,
} from './AddSessionModal/WeightMaterialSection';
import BoxMaterialSection, {
  createEmptyBoxEntry,
  calculateBoxTotals,
  calculateRequiredWeight,
  isBoxFormValid,
  getEffectiveYield,
} from './AddSessionModal/BoxMaterialSection';
import SessionLeftColumn from './AddSessionModal/SessionLeftColumn';
import TransferBackDestinations, { type DestinationRow } from './AddSessionModal/TransferBackDestinations';
import InlineRawMaterialLinking, { type PendingMaterial } from './AddSessionModal/InlineRawMaterialLinking';

interface EditSessionData {
  session_number: string;
  product_sku: string;
  scheduled_date: string;
  transfer_date?: string;
  target_output_weight: number;
  total_boxes: number;
  source_location?: string;
  materials: Array<{
    item_number: string;
    transfer_number: string;
    lot_id?: string | null;
    item_name?: string;
    item_english_name?: string;
    allocated_weight: number;
    cases: number;
    target_output_cases?: number;
    yield_factor: number;
    yield_factor_source?: string;
  }>;
  // WCO-2419: the session's stored active output destinations, used to prefill the transfer-back picker.
  transfer_back_destinations?: Array<{ location_code: string; cases: number | null }>;
}

// WCO-2419: stable signature of the transfer-back picker rows (code + integer cases, order-independent),
// used to decide whether an edit actually changed the destinations/split vs what was stored.
const transferBackSignature = (rows: DestinationRow[]): string =>
  JSON.stringify(
    rows
      .filter(d => d.code)
      .map(d => ({ code: d.code, cases: parseInt(d.cases) || null }))
      .sort((a, b) => a.code.localeCompare(b.code))
  );

interface AddSessionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  sessions?: any[];
  editSession?: EditSessionData | null;
  /** Pre-fill the scheduled date (YYYY-MM-DD) when creating a new session */
  initialDate?: string;
}

const AddSessionModal: React.FC<AddSessionModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  sessions = [],
  editSession = null,
  initialDate,
}) => {
  const { t } = useLanguage();
  const queryClient = useQueryClient();

  // Tab state
  const [inputMode, setInputMode] = useState<InputMode>('boxes');

  const today = new Date().toLocaleDateString('en-CA');
  const startDate = initialDate || today;
  const initialTransfer = getBusinessDayBefore(startDate);
  const [newRow, setNewRow] = useState<NewScheduleRow>({
    product_sku: '',
    scheduled_date: startDate,
    transfer_date: initialTransfer,
  });
  const [transferDateTouched, setTransferDateTouched] = useState(false);

  // Entries for weight mode
  const [weightEntries, setWeightEntries] = useState<WeightMaterialEntry[]>([createEmptyWeightEntry()]);

  // Entries for box mode
  const [boxEntries, setBoxEntries] = useState<BoxMaterialEntry[]>([createEmptyBoxEntry()]);

  // Source warehouse for auto-creating inbound transfer
  const [sourceWarehouse, setSourceWarehouse] = useState<string>('');

  // Transfer back destination for outbound daily transfer (default 'X' = Glendale)
  // WCO-2322 (6A): multiple transfer-back destinations + per-destination case split (was a single dropdown).
  const [transferBackDestinations, setTransferBackDestinations] = useState<DestinationRow[]>([{ code: 'X', cases: '' }]);
  // WCO-2419: signature of the destinations as loaded from the DB when editing (codes + case split). Save
  // compares against this so an untouched edit never re-sends (and thus never rewrites/tombstones) the
  // session's stored destinations, while a real change to a destination OR its case count is persisted.
  const [prefilledDestinationsSignature, setPrefilledDestinationsSignature] = useState<string>(transferBackSignature([]));

  // Sort option for raw materials dropdown
  const [materialSortOption, setMaterialSortOption] = useState<MaterialSortOption>('preference');

  // Track unlinked product data (selected from async search, not in linked products list)
  const [unlinkedProduct, setUnlinkedProduct] = useState<Product | null>(null);

  // Pending raw material mappings (deferred until Save)
  const [pendingMappings, setPendingMappings] = useState<PendingMaterial[]>([]);

  // Loading state for creating mappings on Save
  const [savingMappings, setSavingMappings] = useState(false);

  // Safe quantity confirmation modal state
  const [safeQtyModal, setSafeQtyModal] = useState<{
    /** 'capped' → existing reduce-to-2-CS confirm; 'out_of_stock' → out-of-stock override prompt. */
    severity: 'capped' | 'out_of_stock';
    items: Array<{ itemNumber: string; availableBalance: number; safetyQuantity: number }>;
    location: string;
    resolve: (choice: 'reduce' | 'override' | 'cancel') => void;
  } | null>(null);

  // Track if user chose to override safe qty — suppresses backend stockWarnings toast.
  // Also drives the allow_out_of_stock_override flag sent to the backend.
  const safeQtyOverrideRef = useRef(false);

  // Auto-calculate transfer date (previous business day) when production date changes
  useEffect(() => {
    if (!newRow.scheduled_date || transferDateTouched) return;
    const dayBefore = getBusinessDayBefore(newRow.scheduled_date);
    setNewRow(prev => ({ ...prev, transfer_date: dayBefore }));
  }, [newRow.scheduled_date, transferDateTouched]);

  // Fetch location codes for source location dropdown
  const { data: locationCodes = [] } = useQuery(
    ['ics-location-codes'],
    () => inventoryService.getLocationCodes(),
    { staleTime: 60000 }
  );

  // Fetch products
  const { data: products = [] } = useQuery<Product[]>(
    ['products'],
    () => productionService.getProducts(),
    { keepPreviousData: true, staleTime: 60000 }
  );

  // WCO-2322 (6A): target finished-product cases for the transfer-back split validator (presentation only —
  // same value the modal already computes for expected boxes; the backend owns all allocation logic).
  const transferBackTargetCases = useMemo(() => {
    if (inputMode === 'boxes') {
      return boxEntries.reduce((sum, e) => sum + (parseInt(e.target_boxes) || 0), 0);
    }
    return calculateWeightTotals(weightEntries, products, newRow.product_sku).totalBoxes;
  }, [inputMode, boxEntries, weightEntries, products, newRow.product_sku]);

  // WCO-2322 (6A): once materials/target are entered, show Source Location AND Transfer Back Destinations
  // together — for EVERY session (no longer gated to the first session of the day).
  const hasMaterials = useMemo(() => (
    (inputMode === 'weight' && weightEntries.some(e => e.item_number && (parseFloat(e.allocated_weight) || 0) > 0)) ||
    (inputMode === 'boxes' && boxEntries.some(e => e.item_number && (parseInt(e.target_boxes) || 0) > 0))
  ), [inputMode, weightEntries, boxEntries]);

  // WCO-2322 (6A): before Save, every destination row must have a location AND the cases must sum to
  // Target Cases (otherwise the split sent to the backend wouldn't match the produced cases).
  const transferBackCasesValid = useMemo(() => {
    if (!hasMaterials) return true;
    const allHaveCodes = transferBackDestinations.every(d => d.code);
    const sum = transferBackDestinations.reduce((s, d) => s + (parseInt(d.cases) || 0), 0);
    return allHaveCodes && sum === transferBackTargetCases;
  }, [hasMaterials, transferBackDestinations, transferBackTargetCases]);

  // Fetch raw materials for selected product (declared before edit useEffect so it's available)
  const emptyMaterialSummary: MaterialSummary = { items: [], has_available: false, has_on_hold: false };
  const { data: rawMaterialsData, isLoading: loadingMaterials } = useQuery(
    ['rawMaterials', newRow.product_sku],
    () => productionService.getRawMaterials(newRow.product_sku || undefined),
    { enabled: !!newRow.product_sku, keepPreviousData: true, staleTime: 20000 }
  );
  const rawMaterials = rawMaterialsData?.raw_materials ?? [];
  const materialSummary = rawMaterialsData?.material_summary ?? emptyMaterialSummary;

  // Pre-fill form header (product, dates) when editing — runs immediately
  useEffect(() => {
    if (!isOpen || !editSession) return;

    const schedDate = editSession.scheduled_date?.split('T')[0] || new Date().toLocaleDateString('en-CA');
    const editTransfer = editSession.transfer_date?.split('T')[0] || getBusinessDayBefore(schedDate);
    setTransferDateTouched(!!editSession.transfer_date);
    setNewRow({
      product_sku: editSession.product_sku,
      scheduled_date: schedDate,
      transfer_date: editTransfer,
    });

    if (editSession.source_location) {
      setSourceWarehouse(editSession.source_location);
    }

    // WCO-2419: prefill the transfer-back picker from the session's stored output destinations so the
    // manager sees what is actually saved (was always showing the default [X]). Record the stored codes
    // as the dirty baseline. When the session has no stored destinations, keep the default single row.
    const storedDestinations = editSession.transfer_back_destinations ?? [];
    const prefillRows: DestinationRow[] = storedDestinations.length > 0
      ? storedDestinations.map(d => ({ code: d.location_code, cases: d.cases != null ? String(d.cases) : '' }))
      : [{ code: 'X', cases: '' }];
    setTransferBackDestinations(prefillRows);
    setPrefilledDestinationsSignature(transferBackSignature(prefillRows));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, editSession]);

  // Pre-fill materials when editing — waits for rawMaterials to load for enrichment
  const [editMaterialsLoaded, setEditMaterialsLoaded] = useState(false);
  useEffect(() => {
    if (!isOpen || !editSession) { setEditMaterialsLoaded(false); return; }
    if (!editSession.materials?.length || rawMaterials.length === 0 || products.length === 0 || editMaterialsLoaded) return;

    const product = products.find(p => p.sku === editSession.product_sku);
    const unitWeight = product?.unit_weight || 0;

    // Calculate lot-backed allocation per item (materials with lot_id were deducted from a lot
    // and will be restored on save — so they count as "available" from the edit perspective)
    const lotBackedAllocation = new Map<string, number>();
    for (const m of editSession.materials) {
      if (m.lot_id) {
        const prev = lotBackedAllocation.get(m.item_number) || 0;
        lotBackedAllocation.set(m.item_number, prev + (parseFloat(String(m.allocated_weight)) || 0));
      }
    }

    // Merge materials by item_number (lot-backed + transfer-backed rows are stored separately)
    const mergedByItem = new Map<string, typeof editSession.materials[0]>();
    for (const m of editSession.materials) {
      const existing = mergedByItem.get(m.item_number);
      if (existing) {
        existing.allocated_weight = (existing.allocated_weight || 0) + (m.allocated_weight || 0);
        existing.cases = (existing.cases || 0) + (m.cases || 0);
        if (m.target_output_cases && !existing.target_output_cases) {
          existing.target_output_cases = m.target_output_cases;
        }
        if (m.transfer_number && !existing.transfer_number) {
          existing.transfer_number = m.transfer_number;
        }
      } else {
        mergedByItem.set(m.item_number, { ...m });
      }
    }
    const mergedMaterials = Array.from(mergedByItem.values());

    const hasBoxData = editSession.materials.some(m => m.cases > 0) && unitWeight > 0;

    if (hasBoxData) {
      setInputMode('boxes');
      setBoxEntries(mergedMaterials.map(m => {
        const rm = rawMaterials.find(r => r.item_number === m.item_number);
        const sessionAllocation = lotBackedAllocation.get(m.item_number) || 0;
        return {
          item_number: m.item_number,
          transfer_number: m.transfer_number || '',
          item_name: m.item_english_name || m.item_name || m.item_number,
          target_boxes: String(m.target_output_cases || (mergedMaterials.length === 1 ? editSession.total_boxes : 0) || 0),
          available_stock: (parseFloat(String(rm?.current_stock)) || 0) + sessionAllocation,
          yield_factor: parseFloat(String(m.yield_factor)) || 0.85,
          yield_factor_source: m.yield_factor_source,
          unit: 'lb',
          unit_weight: rm?.unit_weight || unitWeight,
          is_on_hold: false,
        };
      }));
    } else {
      setInputMode('weight');
      setWeightEntries(mergedMaterials.map(m => {
        const rm = rawMaterials.find(r => r.item_number === m.item_number);
        const sessionAllocation = lotBackedAllocation.get(m.item_number) || 0;
        return {
          item_number: m.item_number,
          transfer_number: m.transfer_number || '',
          item_name: m.item_english_name || m.item_name || m.item_number,
          allocated_weight: String(m.allocated_weight || 0),
          available_stock: (parseFloat(String(rm?.current_stock)) || 0) + sessionAllocation,
          yield_factor: parseFloat(String(m.yield_factor)) || 0.85,
          yield_factor_source: m.yield_factor_source,
          unit: 'lb',
          // Carry raw material unit weight so calculateEstimatedBoxes divides by
          // lbs-per-raw-case, not lbs-per-finished-product-case.
          unit_weight: parseFloat(String(rm?.unit_weight)) || undefined,
          is_on_hold: false,
        };
      }));
    }
    setEditMaterialsLoaded(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, editSession, products, rawMaterials, editMaterialsLoaded]);

  // Fetch ICS inventory balances for all raw materials
  const rawMaterialItemNumbers = useMemo(
    () => [...new Set(rawMaterials.map(m => m.item_number))],
    [rawMaterials]
  );

  // True when at least one selected material needs an inbound transfer: either
  // it has no lot backing (`transfer_number` unset) or the request exceeds its
  // available stock. When false, the Source Location dropdown is hidden and a
  // reassurance banner is shown instead.
  const needsInboundTransfer = useMemo(() => {
    if (inputMode === 'weight') {
      return weightEntries.some(e => {
        if (!e.item_number) return false;
        if (!e.transfer_number) return true;
        const lbs = parseFloat(e.allocated_weight) || 0;
        return lbs > e.available_stock;
      });
    }
    return boxEntries.some(e => {
      if (!e.item_number) return false;
      if (!e.transfer_number) return true;
      const required = calculateRequiredWeight(e, products, newRow.product_sku, rawMaterials);
      return required > e.available_stock;
    });
  }, [inputMode, weightEntries, boxEntries, products, newRow.product_sku, rawMaterials]);
  const { data: icsBalances, isLoading: loadingIcsBalances } = useQuery(
    ['icsBalances', rawMaterialItemNumbers],
    () => productionService.getInventoryBalanceBatch(rawMaterialItemNumbers),
    { enabled: rawMaterialItemNumbers.length > 0, staleTime: 60000 }
  );

  // Sync pending material mappings → box/weight entries (for inline linking flow)
  useEffect(() => {
    // When all pending mappings are removed and no raw materials exist, clear entries
    if (pendingMappings.length === 0 && rawMaterials.length === 0) {
      if (inputMode === 'boxes') {
        setBoxEntries(prev => prev.some(e => e.item_number) ? [createEmptyBoxEntry()] : prev);
      } else {
        setWeightEntries(prev => prev.some(e => e.item_number) ? [createEmptyWeightEntry()] : prev);
      }
      return;
    }
    if (pendingMappings.length === 0 || rawMaterials.length > 0) return;
    const product = products.find(p => p.sku === newRow.product_sku);
    const unitWeight = product?.unit_weight || 0;

    if (inputMode === 'boxes') {
      setBoxEntries(prev => {
        return pendingMappings.map(pm => {
          const existing = prev.find(e => e.item_number === pm.item_number);
          if (existing) return existing;
          return {
            ...createEmptyBoxEntry(),
            item_number: pm.item_number,
            item_name: pm.item_english_name || pm.item_number,
            unit_weight: unitWeight,
          };
        });
      });
    } else {
      setWeightEntries(prev => {
        return pendingMappings.map(pm => {
          const existing = prev.find(e => e.item_number === pm.item_number);
          if (existing) return existing;
          const rm = rawMaterials.find(m => m.item_number === pm.item_number);
          return {
            ...createEmptyWeightEntry(),
            item_number: pm.item_number,
            item_name: pm.item_english_name || pm.item_number,
            // Set unit_weight immediately if rawMaterials already loaded, so
            // calculateEstimatedBoxes has the correct divisor from the start.
            unit_weight: rm ? (parseFloat(String(rm.unit_weight)) || undefined) : undefined,
          };
        });
      });
    }
  }, [pendingMappings, rawMaterials.length, inputMode, products, newRow.product_sku]);

  // Enrich entries with raw material data when rawMaterials query refreshes (after inline linking)
  useEffect(() => {
    if (rawMaterials.length === 0) return;
    if (inputMode === 'boxes') {
      setBoxEntries(prev => {
        const needsEnrich = prev.some(e => e.item_number && (!e.yield_factor || e.yield_factor === 0));
        if (!needsEnrich) return prev;
        return prev.map(e => {
          if (!e.item_number || (e.yield_factor && e.yield_factor > 0)) return e;
          const rm = rawMaterials.find(m => m.item_number === e.item_number);
          if (!rm) return e;
          return {
            ...e,
            yield_factor: parseFloat(String(rm.yield_factor)) || 0,
            yield_factor_source: rm.yield_factor_source,
            unit_weight: parseFloat(String(rm.unit_weight)) || e.unit_weight,
            available_stock: parseFloat(String(rm.current_stock)) || 0,
            transfer_number: rm.transfer_number || e.transfer_number,
          };
        });
      });
    } else {
      setWeightEntries(prev => {
        const needsEnrich = prev.some(e => e.item_number && (!e.yield_factor || e.yield_factor === 0));
        if (!needsEnrich) return prev;
        return prev.map(e => {
          if (!e.item_number || (e.yield_factor && e.yield_factor > 0)) return e;
          const rm = rawMaterials.find(m => m.item_number === e.item_number);
          if (!rm) return e;
          return {
            ...e,
            yield_factor: parseFloat(String(rm.yield_factor)) || 0,
            yield_factor_source: rm.yield_factor_source,
            available_stock: parseFloat(String(rm.current_stock)) || 0,
            transfer_number: rm.transfer_number || e.transfer_number,
            // Raw material unit weight for correct case count calculation in transfer requests.
            unit_weight: parseFloat(String(rm.unit_weight)) || e.unit_weight,
          };
        });
      });
    }
  }, [rawMaterials, inputMode]);

  // Fetch manpower summary for the selected date
  const { data: manpowerData, isLoading: loadingManpower } = useQuery(
    ['manpower', newRow.scheduled_date],
    () => workScheduleService.getManpowerSummary(newRow.scheduled_date),
    { enabled: !!newRow.scheduled_date, staleTime: 30000 }
  );

  // Fetch all byproduct-to-raw-material mappings
  const { data: byproductMappings = [] } = useQuery<ByproductMapping[]>(
    ['byproductMappings'],
    () => byproductMappingService.getMappings(),
    { staleTime: 5 * 60 * 1000 }
  );

  // Fetch packing materials for selected product
  const { data: packingData } = useQuery(
    ['packingMaterialsForSession', newRow.product_sku],
    () => packingMaterialMappingService.getPackingMaterialsForSession(newRow.product_sku),
    { enabled: !!newRow.product_sku, staleTime: 30000 }
  );

  // Compute packing material quantities needed and stock sufficiency
  const packingMaterialsDisplay = useMemo(() => {
    if (!packingData?.materials?.length) return [];

    const product = products.find(p => p.sku === newRow.product_sku);
    let expectedBoxes = 0;
    if (inputMode === 'boxes') {
      expectedBoxes = boxEntries.reduce((sum, e) => sum + (parseInt(e.target_boxes) || 0), 0);
    } else {
      expectedBoxes = calculateWeightTotals(weightEntries, products, newRow.product_sku).totalBoxes;
    }

    const productSubQuantity = packingData.product_sub_quantity || 1;

    return packingData.materials.map((mat: PackingMaterialForSession) => {
      if (expectedBoxes <= 0) {
        return { ...mat, piecesNeeded: 0, transferUnitsNeeded: 0, hasEnoughStock: true };
      }

      const piecesNeeded = mat.packing_level === 'subunit'
        ? expectedBoxes * productSubQuantity
        : expectedBoxes;

      let packingSubQty = mat.item_sub_quantity || 0;
      if (packingSubQty <= 1 && mat.packing_info) {
        const match = mat.packing_info.match(/^(\d+)/);
        if (match) packingSubQty = parseInt(match[1]);
      }

      const availablePieces = Number(mat.available_pieces) || 0;
      const hasEnoughStock = availablePieces >= piecesNeeded;
      const shortfallPieces = Math.max(0, piecesNeeded - availablePieces);
      const transferUnitsNeeded = packingSubQty > 1
        ? Math.ceil(shortfallPieces / packingSubQty)
        : shortfallPieces;

      return { ...mat, piecesNeeded, transferUnitsNeeded, hasEnoughStock };
    });
  }, [packingData, newRow.product_sku, products, inputMode, boxEntries, weightEntries]);

  // For edit mode: lot-backed allocation per item that will be restored on save
  const editStockAdjustments = useMemo(() => {
    if (!editSession) return undefined;
    const adj = new Map<string, number>();
    for (const m of editSession.materials) {
      if (m.lot_id) {
        const prev = adj.get(m.item_number) || 0;
        adj.set(m.item_number, prev + (parseFloat(String(m.allocated_weight)) || 0));
      }
    }
    return adj.size > 0 ? adj : undefined;
  }, [editSession]);

  // Sort raw materials based on selected sort option
  const sortedRawMaterials = useMemo(() => {
    if (!rawMaterials.length) return [];
    const getYield = (m: RawMaterial) => parseFloat(String(m.yield_factor)) || 0;
    const getLabor = (m: RawMaterial) => parseFloat(String(m.labour_factor ?? m.labor_factor)) || 0;
    const getStock = (m: RawMaterial) => parseFloat(String(m.current_stock)) || 0;

    return [...rawMaterials].sort((a, b) => {
      switch (materialSortOption) {
        case 'preference': {
          const prefA = a.preference || 999;
          const prefB = b.preference || 999;
          if (prefA !== prefB) return prefA - prefB;
          return getYield(b) - getYield(a);
        }
        case 'yield_high': return getYield(b) - getYield(a);
        case 'yield_low': return getYield(a) - getYield(b);
        case 'labor_high': return getLabor(b) - getLabor(a);
        case 'labor_low': return getLabor(a) - getLabor(b);
        case 'availability_high': return getStock(b) - getStock(a);
        case 'availability_low': return getStock(a) - getStock(b);
        default: return getYield(b) - getYield(a);
      }
    });
  }, [rawMaterials, materialSortOption]);

  const selectedLabourFactorMaterials = useMemo<SelectedLabourFactorMaterial[]>(() => {
    const entries = inputMode === 'boxes' ? boxEntries : weightEntries;
    const seen = new Set<string>();
    // Product unit weight needed for box-mode raw-input calculation
    const productUnitWeight = (products.find(p => p.sku === newRow.product_sku) ?? unlinkedProduct)?.unit_weight ?? 0;

    return entries.reduce<SelectedLabourFactorMaterial[]>((acc, entry) => {
      if (!entry.item_number || seen.has(entry.item_number)) return acc;

      const material = rawMaterials.find(m =>
        m.item_number === entry.item_number &&
        (!entry.transfer_number || m.transfer_number === entry.transfer_number) &&
        (!entry.location_code || m.location_code === entry.location_code)
      ) || rawMaterials.find(m => m.item_number === entry.item_number);
      const labourFactorValue = material?.labour_factor ?? null;
      const labourFactor = labourFactorValue == null ? null : Number(labourFactorValue);
      const yf = material?.yield_factor ?? entry.yield_factor ?? 0;

      // Box mode: mirror the right panel's ceiling-rounded case math so both panels agree.
      // Step 1: theoreticalInput = (target_boxes × product_unit_weight) / yield_factor
      // Step 2: round up to whole raw-material cases (same as BoxMaterialSection estCases)
      // Step 3: inputWeight = rounded_cases × raw_material_unit_weight
      // Weight mode: raw input = allocated_weight (already raw material lbs)
      const inputWeight = inputMode === 'boxes'
        ? (() => {
            const rawBoxes = parseFloat((entry as BoxMaterialEntry).target_boxes || '0');
            const rawUnitWeight = (entry as BoxMaterialEntry).unit_weight || 0;
            if (yf <= 0 || rawUnitWeight <= 0 || productUnitWeight <= 0) return 0;
            const theoreticalInput = (rawBoxes * productUnitWeight) / yf;
            const rawMatCases = Math.ceil(theoreticalInput / rawUnitWeight);
            return rawMatCases * rawUnitWeight;
          })()
        : parseFloat((entry as WeightMaterialEntry).allocated_weight || '0');

      seen.add(entry.item_number);
      acc.push({
        item_number: entry.item_number,
        item_name: material?.item_english_name || entry.item_name || entry.item_number,
        labour_factor: Number.isFinite(labourFactor) && labourFactor! > 0 ? labourFactor : null,
        labour_factor_reference_id: material?.labour_factor_reference_id ?? null,
        labour_factor_reference_scope: material?.labour_factor_reference_scope ?? null,
        labour_factor_source: material?.labour_factor_source ?? null,
        yield_factor: yf,
        input_weight: Number.isFinite(inputWeight) ? inputWeight : 0,
        baseline_throughput: material?.labour_factor_baseline_throughput != null
          ? Number(material.labour_factor_baseline_throughput)
          : null,
        allowance_pct: material?.allowance_pct != null ? Number(material.allowance_pct) : 0,
      });

      return acc;
    }, []);
  }, [inputMode, boxEntries, weightEntries, rawMaterials, products, newRow.product_sku, unlinkedProduct]);

  // Create session mutation
  const createSessionMutation = useMutation(
    productionService.createProductionSession,
    {
      onSuccess: (data: any) => {
        queryClient.invalidateQueries(['productionSessions']);
        queryClient.invalidateQueries(['rawMaterials']);
        resetForm();
        toast.success('Schedule created successfully');
        if (!safeQtyOverrideRef.current && data?.stockWarnings?.length > 0) {
          for (const w of data.stockWarnings) {
            toast(`${w.itemNumber}: ${w.message || 'Below safe quantity'}`, { icon: '⚠️', duration: 6000 });
          }
        }
        safeQtyOverrideRef.current = false;
        onSuccess?.();
        onClose();
      },
      onError: (error: any) => {
        const data = error?.response?.data;
        const errorMessage = data?.error
          || (Array.isArray(data?.errors) ? data.errors.join('; ') : null)
          || error?.message || 'Failed to create schedule';
        toast.error(errorMessage);
      },
    }
  );

  // Update session mutation (used in edit mode)
  const updateSessionMutation = useMutation(
    ({ sessionId, data }: { sessionId: string; data: any }) =>
      productionService.updateProductionSession(sessionId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['productionSessions']);
        queryClient.invalidateQueries(['rawMaterials']);
        resetForm();
        toast.success(t('supervisor.sessionUpdatedSuccess'));
        onSuccess?.();
        onClose();
      },
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.error || error?.message || t('supervisor.failedToUpdateSession');
        toast.error(errorMessage);
      },
    }
  );

  const isEditMode = !!editSession;
  const activeMutation = isEditMode ? updateSessionMutation : createSessionMutation;

  const resetForm = () => {
    const todayStr = new Date().toLocaleDateString('en-CA');
    const resetTransfer = getBusinessDayBefore(todayStr);
    setNewRow({ product_sku: '', scheduled_date: todayStr, transfer_date: resetTransfer });
    setTransferDateTouched(false);
    setWeightEntries([createEmptyWeightEntry()]);
    setBoxEntries([createEmptyBoxEntry()]);
    setSourceWarehouse('');
    setTransferBackDestinations([{ code: 'X', cases: '' }]);
    setPrefilledDestinationsSignature(transferBackSignature([]));
    setUnlinkedProduct(null);
    setPendingMappings([]);
  };

  const handleProductChange = async (sku: string) => {
    setNewRow(prev => ({ ...prev, product_sku: sku }));
    setWeightEntries([createEmptyWeightEntry()]);
    setBoxEntries([createEmptyBoxEntry()]);
    setPendingMappings([]);

    // If the selected product is not in the linked products list, fetch its data
    if (sku && !products.find(p => p.sku === sku)) {
      try {
        const results = await productionService.searchAllProducts(sku);
        const match = results.find((p: Product) => p.sku === sku);
        setUnlinkedProduct(match || null);
      } catch {
        setUnlinkedProduct(null);
      }
    } else {
      setUnlinkedProduct(null);
    }
  };

  // Wrapper for updateBoxMaterial needed by SessionLeftColumn (target_boxes input)
  const updateBoxMaterial = (index: number, field: string, value: string) => {
    if (field === 'target_boxes') {
      setBoxEntries(prev => prev.map((entry, i) =>
        i === index ? { ...entry, target_boxes: value } : entry
      ));
    }
  };

  // ============ SAFE QUANTITY PRE-CHECK ============
  // Checks transfer-check API for each material needing a transfer.
  // If any are capped, shows modal to reduce to 2 CS or cancel.
  // Returns the (possibly adjusted) payload, or null to abort.
  const checkSafeQuantities = async (
    materials: Array<{ item_number: string; cases: number; allocated_weight: number }>,
    location: string,
  ): Promise<Array<{ item_number: string; cases: number; allocated_weight: number }> | null> => {
    try {
      const results = await Promise.all(
        materials.map(m =>
          api.get(`/inventory-balance/transfer-check/${encodeURIComponent(m.item_number)}`, {
            params: { quantity: m.cases, location },
          }).then(r => r.data).catch(() => null)
        )
      );

      const capped = results.filter(r => r && r.decision === 'capped');
      const outOfStock = results.filter(r => r && r.decision === 'out_of_stock');

      if (outOfStock.length > 0) {
        // Stock API is currently unreliable — give the manager an override path
        // instead of a hard block. Same modal pattern as capped, but with
        // out_of_stock severity (only Cancel + Override, no Reduce-to-2-CS).
        const choice = await new Promise<'reduce' | 'override' | 'cancel'>((resolve) => {
          setSafeQtyModal({
            severity: 'out_of_stock',
            items: outOfStock.map((r: any) => ({
              itemNumber: r.itemNumber,
              availableBalance: r.availableBalance,
              safetyQuantity: r.safetyQuantity,
            })),
            location,
            resolve,
          });
        });
        setSafeQtyModal(null);
        if (choice === 'cancel') return null;
        // Treat 'override' (and the unused 'reduce' for safety) as override —
        // proceed with original quantities and tell the backend to skip its check.
        safeQtyOverrideRef.current = true;
        return materials;
      }

      if (capped.length > 0) {
        // Show modal and wait for user decision
        const choice = await new Promise<'reduce' | 'override' | 'cancel'>((resolve) => {
          setSafeQtyModal({
            severity: 'capped',
            items: capped.map((r: any) => ({
              itemNumber: r.itemNumber,
              availableBalance: r.availableBalance,
              safetyQuantity: r.safetyQuantity,
            })),
            location,
            resolve,
          });
        });

        setSafeQtyModal(null);
        if (choice === 'cancel') return null;
        if (choice === 'override') {
          safeQtyOverrideRef.current = true;
          return materials;
        }

        // Adjust capped items to 2 cases
        return materials.map(m => {
          const check = capped.find((r: any) => r.itemNumber === m.item_number);
          if (check) {
            const unitWeight = m.allocated_weight / (m.cases || 1);
            return { ...m, cases: 2, allocated_weight: 2 * unitWeight };
          }
          return m;
        });
      }

      return materials;
    } catch {
      // Fail-open: if check fails, proceed without blocking
      return materials;
    }
  };

  // ============ SAVE HANDLER ============
  const handleSave = async () => {
    if (!newRow.product_sku) {
      toast.error(t('supervisor.selectProductError') || 'Please select a product');
      return;
    }

    // Reject past scheduled dates
    if (newRow.scheduled_date && newRow.scheduled_date < today) {
      toast.error(t('supervisor.pastDateError') || 'Scheduled date cannot be in the past');
      return;
    }

    // Note: raw material mappings are saved immediately when selected in InlineRawMaterialLinking.
    // No deferred save needed here.

    // WCO-2419: decide whether to send transfer_back_destinations. When creating, always send it.
    // When editing, send ONLY if the manager changed the destinations vs what was stored — either a
    // different set of locations OR a different per-destination case count (signature covers both). An
    // untouched edit omits the field entirely so the backend leaves the stored outputs alone. The picker
    // preserves prefilled/edited case splits (see casesAreAuthoritative), so this comparison is stable.
    const destinationsChanged = transferBackSignature(transferBackDestinations) !== prefilledDestinationsSignature;
    const transferBackDestinationsPayload =
      isEditMode && !destinationsChanged
        ? undefined
        : hasMaterials
          ? transferBackDestinations.filter(d => d.code).map(d => ({ location_code: d.code, cases: parseInt(d.cases) || null }))
          : undefined;

    if (inputMode === 'weight') {
      const validMaterials = weightEntries.filter(entry =>
        entry.item_number && entry.allocated_weight && parseFloat(entry.allocated_weight) > 0
      );
      if (validMaterials.length === 0) {
        toast.error(t('supervisor.selectMaterialError') || 'Please add at least one raw material with weight');
        return;
      }

      const { totalBoxes, totalOutputWeight } = calculateWeightTotals(weightEntries, products, newRow.product_sku);

      const rawMaterialsPayload = validMaterials.map(entry => ({
        item_number: entry.item_number,
        transfer_number: entry.transfer_number || null,
        allocated_weight: parseFloat(entry.allocated_weight) || 0,
        cases: calculateEstimatedBoxes(entry, products, newRow.product_sku),
        is_on_hold: entry.is_on_hold,
      }));

      const payload = {
        product_sku: newRow.product_sku,
        raw_materials: rawMaterialsPayload,
        target_output_weight: totalOutputWeight,
        total_boxes: totalBoxes,
        scheduled_date: newRow.scheduled_date,
        source_warehouse: (isEditMode || needsInboundTransfer) ? (sourceWarehouse || undefined) : undefined,
        transfer_back_destinations: transferBackDestinationsPayload,
        transfer_date: newRow.transfer_date,
      };

      if (isEditMode) {
        updateSessionMutation.mutate({ sessionId: editSession!.session_number, data: payload });
      } else {
        // Pre-check safe quantities if transfer is needed
        if (needsInboundTransfer && sourceWarehouse) {
          const checked = await checkSafeQuantities(rawMaterialsPayload, sourceWarehouse);
          if (!checked) return;
          const adjusted = checked.map((m, i) => ({ ...rawMaterialsPayload[i], allocated_weight: m.allocated_weight, cases: m.cases }));
          payload.raw_materials = adjusted;
          // Recalculate output if material weight was reduced
          const newTotalWeight = adjusted.reduce((sum, m) => sum + m.allocated_weight, 0);
          if (newTotalWeight !== rawMaterialsPayload.reduce((sum, m) => sum + m.allocated_weight, 0)) {
            const yieldFactor = weightEntries[0]?.yield_factor || 1;
            payload.target_output_weight = newTotalWeight * yieldFactor;
            const prodUnit = products.find(p => p.sku === newRow.product_sku)?.unit_weight || 1;
            payload.total_boxes = Math.floor(payload.target_output_weight / prodUnit);
          }
        }
        // Forward the out-of-stock override flag (set in checkSafeQuantities)
        // so the backend skips its own stock check.
        if (safeQtyOverrideRef.current) {
          (payload as any).allow_out_of_stock_override = true;
        }
        createSessionMutation.mutate(payload);
      }
    } else {
      const validMaterials = boxEntries.filter(entry =>
        entry.item_number && entry.target_boxes && parseInt(entry.target_boxes) > 0
      );
      if (validMaterials.length === 0) {
        toast.error(t('supervisor.selectMaterialError') || 'Please add at least one raw material with boxes');
        return;
      }

      const { totalBoxes, totalUsableOutput } = calculateBoxTotals(boxEntries, products, newRow.product_sku, rawMaterials);
      const product = products.find(p => p.sku === newRow.product_sku);

      const rawMaterialsPayload = validMaterials.map(entry => {
        const boxes = parseInt(entry.target_boxes) || 0;
        const allocatedWeight = calculateRequiredWeight(entry, products, newRow.product_sku, rawMaterials);
        // cases = raw material cases for transfer (input weight based), not finished product boxes
        const rawMaterialCases = entry.unit_weight > 0 ? Math.ceil(allocatedWeight / entry.unit_weight) : boxes;
        return {
          item_number: entry.item_number,
          transfer_number: entry.transfer_number || null,
          allocated_weight: allocatedWeight,
          cases: rawMaterialCases,
          is_on_hold: entry.is_on_hold,
          target_boxes: boxes,
        };
      });

      let targetOutput = totalUsableOutput;
      if (targetOutput <= 0 && totalBoxes > 0 && product?.unit_weight) {
        targetOutput = totalBoxes * product.unit_weight;
      }

      const payload = {
        product_sku: newRow.product_sku,
        raw_materials: rawMaterialsPayload,
        target_output_weight: targetOutput,
        total_boxes: totalBoxes,
        scheduled_date: newRow.scheduled_date,
        source_warehouse: (isEditMode || needsInboundTransfer) ? (sourceWarehouse || undefined) : undefined,
        transfer_back_destinations: transferBackDestinationsPayload,
        transfer_date: newRow.transfer_date,
      };

      if (isEditMode) {
        updateSessionMutation.mutate({ sessionId: editSession!.session_number, data: payload });
      } else {
        // Pre-check safe quantities if transfer is needed
        if (needsInboundTransfer && sourceWarehouse) {
          const checked = await checkSafeQuantities(rawMaterialsPayload, sourceWarehouse);
          if (!checked) return;
          const adjusted = checked.map((m, i) => ({ ...rawMaterialsPayload[i], allocated_weight: m.allocated_weight, cases: m.cases }));
          payload.raw_materials = adjusted;
          // Recalculate output if material weight was reduced
          const newTotalWeight = adjusted.reduce((sum, m) => sum + m.allocated_weight, 0);
          if (newTotalWeight !== rawMaterialsPayload.reduce((sum, m) => sum + m.allocated_weight, 0)) {
            const rm = rawMaterials.find((r: RawMaterial) => r.item_number === adjusted[0]?.item_number);
            const yieldFactor = rm?.yield_factor || 1;
            payload.target_output_weight = newTotalWeight * yieldFactor;
            payload.total_boxes = product?.unit_weight ? Math.floor(payload.target_output_weight / product.unit_weight) : 0;
          }
        }
        // Forward the out-of-stock override flag (set in checkSafeQuantities)
        // so the backend skips its own stock check.
        if (safeQtyOverrideRef.current) {
          (payload as any).allow_out_of_stock_override = true;
        }
        createSessionMutation.mutate(payload);
      }
    }
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  const selectedProduct = products.find(p => p.sku === newRow.product_sku) || unlinkedProduct || undefined;
  const boxTotals = calculateBoxTotals(boxEntries, products, newRow.product_sku, rawMaterials);

  const isFormValid = inputMode === 'weight'
    ? isWeightFormValid(weightEntries, newRow.product_sku, sourceWarehouse)
    : isBoxFormValid(boxEntries, products, newRow.product_sku, rawMaterials, sourceWarehouse);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={handleCancel}
        />

        {/* Modal */}
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-[95vw] max-h-[90vh] flex flex-col p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {isEditMode
                ? `${t('supervisor.editProductionSession')} ${editSession!.session_number}`
                : t('supervisor.addNewSchedule')}
            </h3>
            <button
              onClick={handleCancel}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200 dark:border-gray-700 mb-3">
            <button
              onClick={() => setInputMode('boxes')}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                inputMode === 'boxes'
                  ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <Package className="w-4 h-4" />
              {t('supervisor.byBoxes') || 'By Boxes'}
            </button>
            <button
              onClick={() => setInputMode('weight')}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                inputMode === 'weight'
                  ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <Scale className="w-4 h-4" />
              {t('supervisor.byWeight') || 'By Weight'}
            </button>
          </div>

          <div className="flex-1 min-h-0 grid grid-cols-[2fr_3fr] gap-4 overflow-hidden">
            {/* LEFT COLUMN */}
            <SessionLeftColumn
              newRow={newRow}
              setNewRow={setNewRow}
              onTransferDateChange={(v) => {
                setTransferDateTouched(true);
                setNewRow(prev => ({ ...prev, transfer_date: v }));
              }}
              products={products}
              selectedProduct={selectedProduct}
              inputMode={inputMode}
              boxEntries={boxEntries}
              updateBoxMaterial={updateBoxMaterial}
              boxTotalBoxes={boxTotals.totalBoxes}
              handleProductChange={handleProductChange}
              manpowerData={manpowerData}
              loadingManpower={loadingManpower}
              packingMaterialsDisplay={packingMaterialsDisplay}
              selectedLabourFactorMaterials={selectedLabourFactorMaterials}
            />

            {/* RIGHT COLUMN: Raw Materials (scrollable) */}
            <div className="overflow-y-auto min-w-0 pr-1 border-l border-gray-200 dark:border-gray-700 pl-4">
              {/* Inline raw material linking UI — shown when product has no materials */}
              {newRow.product_sku && !loadingMaterials && (rawMaterials.length === 0 || pendingMappings.length > 0) && (
                <InlineRawMaterialLinking
                  productSku={newRow.product_sku}
                  productName={selectedProduct?.name || newRow.product_sku}
                  onPendingMaterialsChange={setPendingMappings}
                />
              )}

              {inputMode === 'weight' && (
                <WeightMaterialSection
                  weightEntries={weightEntries}
                  setWeightEntries={setWeightEntries}
                  productSku={newRow.product_sku}
                  products={products}
                  rawMaterials={rawMaterials}
                  sortedRawMaterials={sortedRawMaterials}
                  loadingMaterials={loadingMaterials}
                  materialSortOption={materialSortOption}
                  setMaterialSortOption={setMaterialSortOption}
                  byproductMappings={byproductMappings}
                  materialSummary={materialSummary}
                  icsBalances={icsBalances}
                  loadingIcsBalances={loadingIcsBalances}
                  sourceWarehouse={sourceWarehouse}
                  stockAdjustments={editStockAdjustments}
                />
              )}

              {inputMode === 'boxes' && (
                <BoxMaterialSection
                  boxEntries={boxEntries}
                  setBoxEntries={setBoxEntries}
                  productSku={newRow.product_sku}
                  products={products}
                  rawMaterials={rawMaterials}
                  sortedRawMaterials={sortedRawMaterials}
                  loadingMaterials={loadingMaterials}
                  materialSortOption={materialSortOption}
                  setMaterialSortOption={setMaterialSortOption}
                  byproductMappings={byproductMappings}
                  materialSummary={materialSummary}
                  icsBalances={icsBalances}
                  loadingIcsBalances={loadingIcsBalances}
                  sourceWarehouse={sourceWarehouse}
                  stockAdjustments={editStockAdjustments}
                />
              )}

              {/* Source Warehouse & Transfer Back Destination — side by side (shown once materials entered) */}
              {hasMaterials && (
                <div className="pt-3 mt-3 border-t border-gray-200 dark:border-gray-600 grid grid-cols-2 gap-4">
                  {/* Source Warehouse — hidden when all selected materials can be covered by on-facility stock */}
                  {((inputMode === 'weight' && weightEntries.some(e => e.item_number && (parseFloat(e.allocated_weight) || 0) > 0)) ||
                    (inputMode === 'boxes' && boxEntries.some(e => e.item_number && (parseInt(e.target_boxes) || 0) > 0))) && (
                    needsInboundTransfer ? (
                      <div>
                        <label
                          className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 cursor-help"
                          title={t('supervisor.sourceWarehouseTooltip')}
                        >
                          {t('supervisor.sourceWarehouse')}
                          {((inputMode === 'weight' && weightEntries.some(e => e.item_number && !e.transfer_number)) ||
                            (inputMode === 'boxes' && boxEntries.some(e => e.item_number && !e.transfer_number))) && ' *'}
                        </label>
                        <select
                          value={sourceWarehouse}
                          onChange={(e) => setSourceWarehouse(e.target.value)}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
                        >
                          <option value="">{t('warehouseTransfer.selectLocation') || 'Select a location...'}</option>
                          {locationCodes.map(loc => (
                            <option key={loc.location_code} value={loc.location_code}>
                              {loc.location_code}{loc.description ? ` - ${loc.description}` : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <div className="p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                        <p className="text-xs text-green-800 dark:text-green-200">
                          {t('supervisor.noInboundTransferNeeded')}
                        </p>
                      </div>
                    )
                  )}

                  {/* WCO-2322 (6A): Transfer Back Destinations (multi, per-destination case split) */}
                  {hasMaterials && (
                    <TransferBackDestinations
                      destinations={transferBackDestinations}
                      onChange={setTransferBackDestinations}
                      targetCases={transferBackTargetCases}
                      locationCodes={locationCodes}
                    />
                  )}
                </div>
              )}

            </div>
          </div>

          {/* Modal Footer */}
          <div className="flex justify-end gap-3 mt-4 pt-3 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
            <button
              onClick={handleCancel}
              disabled={activeMutation.isLoading || savingMappings}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={activeMutation.isLoading || savingMappings || !isFormValid || !transferBackCasesValid}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {(activeMutation.isLoading || savingMappings) ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('common.saving')}...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  {t('common.save')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      {/* Safe Quantity Confirmation Modal */}
      {safeQtyModal && (() => {
        const isOOS = safeQtyModal.severity === 'out_of_stock';
        const headerCls = isOOS
          ? 'text-red-600 dark:text-red-400'
          : 'text-yellow-600 dark:text-yellow-400';
        const rowCls = isOOS
          ? 'bg-red-50 dark:bg-red-900/20'
          : 'bg-yellow-50 dark:bg-yellow-900/20';
        const primaryBtnCls = isOOS
          ? 'bg-red-600 hover:bg-red-700'
          : 'bg-yellow-600 hover:bg-yellow-700';
        return (
          <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className={`text-lg font-semibold ${headerCls} flex items-center gap-2`}>
                  ⚠️ {isOOS ? 'Out of Stock' : 'Below Safe Quantity'}
                </h3>
              </div>
              <div className="p-4">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  {isOOS ? (
                    <>
                      The following items are out of stock at <strong>{safeQtyModal.location}</strong> per the inventory balance API. Stock data may be inaccurate — you can <strong>override and proceed</strong> if you've verified availability in person.
                    </>
                  ) : (
                    <>
                      The following items are below safe quantity at <strong>{safeQtyModal.location}</strong>. Transfer quantity will be reduced to <strong>2 cases</strong>:
                    </>
                  )}
                </p>
                <div className="space-y-2">
                  {safeQtyModal.items.map((item) => (
                    <div key={item.itemNumber} className={`flex justify-between items-center p-2 ${rowCls} rounded-md`}>
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{item.itemNumber}</span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {isOOS
                          ? `${item.availableBalance} CS available`
                          : `${item.availableBalance} CS avail / ${item.safetyQuantity} CS safety`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => safeQtyModal.resolve('cancel')}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 rounded-md transition-colors"
                >
                  Cancel
                </button>
                {!isOOS && (
                  <button
                    onClick={() => safeQtyModal.resolve('override')}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md transition-colors"
                  >
                    Override
                  </button>
                )}
                <button
                  onClick={() => safeQtyModal.resolve(isOOS ? 'override' : 'reduce')}
                  className={`px-4 py-2 text-sm font-medium text-white ${primaryBtnCls} rounded-md transition-colors`}
                >
                  {isOOS ? 'Override and Proceed' : 'OK'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default AddSessionModal;
